import { Button } from "azure-devops-ui/Button";
import { IMenuItem, MenuButton } from "azure-devops-ui/Menu";
import { css, getSafeIdSelector } from "azure-devops-ui/Util";
import { Location } from "azure-devops-ui/Utilities/Position";
import React from "react";
import { ResponsiveLayout } from "../../../common/components/responsivelayout/responsivelayout";
import { NavigationContext } from "../../../common/contexts/navigation";
import { useSubscription } from "../../../common/hooks/useobservable";
import { SessionContext } from "../../contexts/session";
import { UserPreferencesContext } from "../../contexts/userpreferences";
import { getLayoutStyle } from "../../utilities/layout";

const { AlbumsLink, GalleryLink, ExploreLink, PeopleLink } = window.Resources.Common;

export type PivotId = "albums" | "gallery" | "explore" | "people";

interface IPivotConfiguration {
  href: string;
  id: PivotId;
  name: string;
  text: string;
}

export interface IPivotsProps {
  activeId: PivotId;
  className?: string;
}

export function Pivots(props: IPivotsProps): React.ReactElement {
  const { activeId, className } = props;

  const navigationContext = React.useContext(NavigationContext);
  const sessionContext = React.useContext(SessionContext);
  const userPreferencesContext = React.useContext(UserPreferencesContext);

  const pivotElement = React.useRef<HTMLDivElement>(null);

  const photoPreferences = useSubscription(userPreferencesContext.getUserPreferences("photo"));

  const layoutStyle = getLayoutStyle();
  const desktopLayout = layoutStyle === "desktop";
  const pivots = React.useMemo(() => {
    const pivotConfigs: IPivotConfiguration[] = [
      {
        href: "/",
        id: "gallery",
        name: "navigateGalleryPivot",
        text: GalleryLink
      },
      {
        href: "/albums",
        id: "albums",
        name: "navigateAlbumsPivot",
        text: AlbumsLink
      },
      {
        href: "/explore",
        id: "explore",
        name: "navigateExplorePivot",
        text: ExploreLink
      }
    ];

    if (photoPreferences?.faceExperience) {
      pivotConfigs.push({
        href: "/people",
        id: "people",
        name: "navigatePeoplePivot",
        text: PeopleLink
      });
    }
    return pivotConfigs;
  }, [photoPreferences?.faceExperience]);

  const { activeText, buttons, menuItems } = React.useMemo(() => {
    const buttons: React.ReactElement[] = [];
    const menuItems: IMenuItem[] = [];
    let activeText: string | undefined;

    for (let pivotIndex = 0; pivotIndex < pivots.length; pivotIndex++) {
      const pivot = pivots[pivotIndex];

      const href = navigationContext.prepare(pivot.href);
      const isActive = pivot.id === activeId;

      if (isActive) {
        activeText = pivot.text;
      }

      menuItems.push({
        checked: isActive,
        href,
        id: pivot.id,
        onActivate: (_, event) => navigationContext.navigate(event, pivot.name),
        readonly: true,
        text: pivot.text
      });

      buttons.push(
        <Button
          className="rounded-button strong-button"
          href={href}
          id={pivot.id}
          key={pivot.id}
          onClick={(event) => navigationContext.navigate(event, pivot.name)}
          primary={isActive}
          role="menuitem"
          subtle={!isActive}
        >
          {pivot.text}
        </Button>
      );
    }

    return { activeText, buttons, menuItems };
  }, [activeId, navigationContext, pivots]);

  return (
    <ResponsiveLayout
      containerElement={pivotElement}
      key="pivot-layout"
      responsiveSelectors={[getSafeIdSelector("gallery"), getSafeIdSelector("albums"), getSafeIdSelector("explore")]}
    >
      {({ responsiveIndex }) =>
        !sessionContext.embedded ? (
          <div className={css(className, "flex-row flex-align-center rhythm-horizontal-4 overflow-hidden")} ref={pivotElement} role="group">
            {!desktopLayout || responsiveIndex !== -1 ? (
              <MenuButton
                className="rounded-dropdown strong-button"
                contextualMenuProps={{
                  anchorOrigin: { horizontal: Location.start, vertical: Location.end },
                  menuOrigin: { horizontal: Location.start, vertical: Location.start },
                  menuProps: {
                    className: "compact-menu",
                    id: "overflow-menu",
                    items: menuItems
                  }
                }}
                primary={true}
              >
                {activeText}
              </MenuButton>
            ) : (
              buttons
            )}
          </div>
        ) : (
          <div className={css(className, "flex-row flex-align-center")} />
        )
      }
    </ResponsiveLayout>
  );
}
